import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useV2Fonts } from './_shared';

const PROPOSALS = [
  {
    id: 'a',
    name: 'Operational Command Center',
    tagline: 'Linear · Stripe · MaintainX',
    description: 'Light, denso, brutalista. Zero distrazioni, velocità massima per chi vive nei ticket 8 ore al giorno. Numeri grossi, tabelle al posto delle card, 1px border invece di ombre.',
    bg: '#FAFAFA',
    surface: '#FFFFFF',
    primary: '#000000',
    accent: '#0052FF',
    text: '#111111',
    border: '#E5E5E5',
    fontDisplay: '"Cabinet Grotesk", "Space Grotesk", sans-serif',
    fontBody: '"IBM Plex Sans", system-ui, sans-serif',
    pros: ['Velocità d\'uso massima', 'Scalabile a 30+ hotel', 'Manutenibilità top'],
    cons: ['Wow factor medio', 'Sembra "da sviluppatori"'],
  },
  {
    id: 'b',
    name: 'Hospitality Boutique',
    tagline: 'Mews · Apaleo · Cloudbeds',
    description: 'Elegante, spazioso, accogliente. Serif editoriale per i titoli, geometric sans per i dati. Sembra il software di un 5 stelle, non un IT-tool.',
    bg: '#F7F5F0',
    surface: '#FFFFFF',
    primary: '#2C3E3A',
    accent: '#C19A6B',
    text: '#1A1A1A',
    border: '#EAE6DF',
    fontDisplay: '"Cormorant Garamond", "Playfair Display", serif',
    fontBody: '"Outfit", "Inter", sans-serif',
    pros: ['Wow factor altissimo', 'Identità "hotel" forte', 'Distingue dai concorrenti'],
    cons: ['Refactor + lungo', 'Padding ampi = + scroll su mobile'],
  },
  {
    id: 'c',
    name: 'Data Intelligence Workspace',
    tagline: 'Bloomberg · Vercel · Cron',
    description: 'Dark mode tattico, "sala di controllo". Contrasto massimo, neon accents per i dati critici, bento grid denso. Pensato per chi deve leggere KPI in fretta.',
    bg: '#09090B',
    surface: '#121214',
    primary: '#FFFFFF',
    accent: '#3B82F6',
    text: '#F8FAFC',
    border: '#27272A',
    fontDisplay: '"Clash Display", "Space Grotesk", sans-serif',
    fontBody: '"Manrope", "Inter", sans-serif',
    pros: ['Wow factor "sala controllo"', 'Visibilità dati estrema', 'Riposo per gli occhi (uso lungo)'],
    cons: ['Refactor totale dei token', 'Manager non-tech può preferire light'],
  },
];

const fontsHref = ''; // gestiti via useV2Fonts da _shared

const useGoogleFonts = useV2Fonts;

const IndexV2 = () => {
  useGoogleFonts();

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900" data-testid="mockup-v2-index">
      <header className="border-b border-neutral-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500 mb-1">
              <Sparkles className="w-3.5 h-3.5" /> HostFix · Redesign Phase 2
            </div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: '"Space Grotesk", system-ui' }}>
              Scegli la direzione visiva
            </h1>
            <p className="text-sm text-neutral-500 mt-1">
              3 proposte distinte, benchmarkate sui leader di mercato. Apri ognuna, navigala, poi torna qui e scegli.
            </p>
          </div>
          <Link
            to="/mockup"
            className="text-sm text-neutral-500 hover:text-neutral-900 underline underline-offset-4"
            data-testid="back-to-mockup-hub"
          >
            ← Mockup Hub v1
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PROPOSALS.map((p) => (
            <div
              key={p.id}
              className="border border-neutral-200 bg-white overflow-hidden flex flex-col"
              data-testid={`proposal-card-${p.id}`}
            >
              {/* Mini-preview top bar */}
              <div
                className="h-32 relative"
                style={{ background: p.bg, borderBottom: `1px solid ${p.border}` }}
              >
                <div className="absolute inset-4 grid grid-cols-3 gap-2">
                  <div style={{ background: p.surface, border: `1px solid ${p.border}` }} className="rounded-sm" />
                  <div style={{ background: p.surface, border: `1px solid ${p.border}` }} className="rounded-sm" />
                  <div style={{ background: p.accent }} className="rounded-sm opacity-90" />
                </div>
                <div
                  className="absolute top-3 left-3 text-[10px] uppercase tracking-widest font-semibold"
                  style={{ color: p.text, fontFamily: p.fontDisplay }}
                >
                  Proposta {p.id.toUpperCase()}
                </div>
              </div>

              <div className="p-5 flex-1 flex flex-col">
                <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">{p.tagline}</div>
                <h2 className="text-xl font-bold mb-3" style={{ fontFamily: p.fontDisplay, color: p.text }}>
                  {p.name}
                </h2>
                <p className="text-sm text-neutral-600 leading-relaxed mb-4">{p.description}</p>

                <div className="space-y-1.5 mb-4">
                  {p.pros.map((pro) => (
                    <div key={pro} className="flex items-start gap-2 text-xs">
                      <span className="text-green-600 mt-0.5">✓</span>
                      <span className="text-neutral-700">{pro}</span>
                    </div>
                  ))}
                  {p.cons.map((con) => (
                    <div key={con} className="flex items-start gap-2 text-xs">
                      <span className="text-amber-600 mt-0.5">!</span>
                      <span className="text-neutral-500">{con}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-auto pt-4 border-t border-neutral-100 flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-5 h-5 rounded-full border border-neutral-200" style={{ background: p.bg }} />
                    <div className="w-5 h-5 rounded-full border border-neutral-200" style={{ background: p.primary }} />
                    <div className="w-5 h-5 rounded-full border border-neutral-200" style={{ background: p.accent }} />
                  </div>
                  <Link
                    to={`/mockup/v2/${p.id}`}
                    className="ml-auto inline-flex items-center gap-1.5 text-sm font-medium hover:opacity-80"
                    style={{ color: p.accent }}
                    data-testid={`open-proposal-${p.id}`}
                  >
                    Apri proposta <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 p-5 border border-neutral-200 bg-white" data-testid="how-to-choose">
          <h3 className="text-sm font-semibold mb-2" style={{ fontFamily: '"Space Grotesk", system-ui' }}>
            Come scegliere
          </h3>
          <ol className="text-sm text-neutral-600 space-y-1 list-decimal list-inside">
            <li>Apri le 3 proposte in 3 tab diverse del browser (click destro → apri in nuova scheda).</li>
            <li>Confrontale alternando le tab — l&apos;occhio noterà subito quale ti &laquo;torna&raquo; naturale.</li>
            <li>Pensa: &laquo;userei questa app 8 ore al giorno?&raquo; e &laquo;i miei manager si sentirebbero professionali con questa?&raquo;.</li>
            <li>Dimmi la lettera (A, B, C) o un mix (es. &laquo;B per i colori, A per la densità&raquo;) e applico al codice live.</li>
          </ol>
        </div>
      </main>
    </div>
  );
};

export default IndexV2;
